/**
 * Tâche 1251 (Eric 2026-06-28) — Assistant « copie hors site ».
 *
 * Poser des clés OVH ne prouve rien : ce qui compte est qu'une archive soit
 * réellement partie et qu'on l'ait RETROUVÉE dans le bucket. L'assistant
 * enchaîne les quatre étapes et dit laquelle a échoué, avec quoi faire.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, XCircle, CloudArrowUp, SpinnerGap } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Tâche 1395 — Table hors composant : les deux langues sur la MÊME entrée
// (motif 1324b). Un appel au traducteur serait ici hors du hook, donc un
// écran blanc.
const OVH_STEPS = [
  {
    titre: "1. Créer le conteneur",
    titre_en: "1. Create the container",
    detail: "espace client OVH → Public Cloud → Storage → Object Storage → Créer un conteneur. Type S3 (pas Swift), région BHS (Beauharnois, Québec) — obligatoire pour la loi 25 — nom knoria-backups-prod, conteneur PRIVÉ.",
    detail_en: "OVH control panel → Public Cloud → Storage → Object Storage → Create a container. Type S3 (not Swift), region BHS (Beauharnois, Quebec) — required by Law 25 — name knoria-backups-prod, PRIVATE container.",
  },
  {
    titre: "2. Créer l'utilisateur et ses clés",
    titre_en: "2. Create the user and its keys",
    detail: "Public Cloud → Users & Roles → Ajouter un utilisateur (knoria-backup), rôle « ObjectStore operator » (lecture ET écriture : la vérification nocturne relit l'objet). Puis « Générer les identifiants S3 » — notez la clé secrète, elle ne s'affiche qu'une fois.",
    detail_en: "Public Cloud → Users & Roles → Add a user (knoria-backup), role “ObjectStore operator” (read AND write: the nightly check re-reads the object). Then “Generate S3 credentials” — write the secret key down, it is shown only once.",
  },
  {
    titre: "3. Les poser dans Knoria",
    titre_en: "3. Enter them in Knoria",
    detail: "Forfait AI → Clés BYOK → OVH Object Storage : clé d'accès, clé secrète, nom exact du conteneur, région bhs. Laissez le point d'accès vide.",
    detail_en: "AI Package → BYOK keys → OVH Object Storage: access key, secret key, exact container name, region bhs. Leave the endpoint empty.",
  },
  {
    titre: "4. Confirmer ici",
    titre_en: "4. Confirm here",
    detail: "Revenez sur cette carte et lancez l'assistant : il envoie une vraie archive puis va la RECHERCHER dans le conteneur.",
    detail_en: "Come back to this card and run the wizard: it uploads a real archive, then goes and FINDS it in the container.",
  },
];

export const OffsiteSetupCard = ({ onDone }) => {
  const { tr, lang } = useLang();
  // Tâche 1254 — Aucune organisation n'avait de clés OVH : les sauvegardes
  // n'existaient qu'en un exemplaire, sur le disque du serveur. La marche à
  // suivre vit donc À CÔTÉ du bouton, pas dans un document que personne n'ouvre.
  const [guideOpen, setGuideOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/backups/offsite-setup`, {}, {
        withCredentials: true,
        timeout: 300000,
      });
      setResult(data);
      if (data.confirmed) {
        toast.success(tr("Copie hors site confirmée", "Off-site copy confirmed"));
        if (onDone) onDone();
      } else {
        toast.error(tr("L'assistant s'est arrêté à une étape", "The wizard stopped at one step"));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Assistant interrompu", "Wizard interrupted"));
    } finally {
      setRunning(false);
    }
  };

  return (
    <section
      className="mb-6 rounded-lg border border-zinc-200 bg-white p-4"
      data-testid="offsite-setup-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-zinc-900 flex items-center gap-2">
            <CloudArrowUp size={18} weight="duotone" /> {tr("Assistant copie hors site", "Off-site copy wizard")}
          </h3>
          <p className="text-xs text-zinc-600 mt-1 max-w-2xl">
            {tr(
              "Quatre étapes enchaînées : clés OVH, test de connexion, première sauvegarde envoyée, puis archive RETROUVÉE dans le bucket. Rien n'est « confirmé » avant la quatrième.",
              "Four linked steps: OVH keys, connection test, first backup uploaded, then the archive FOUND again in the bucket. Nothing is “confirmed” before the fourth.",
            )}
          </p>
        </div>
        <Button
          onClick={run}
          disabled={running}
          className="flex items-center gap-2"
          data-testid="offsite-setup-run-btn"
        >
          {running ? <SpinnerGap size={16} className="animate-spin" /> : <CloudArrowUp size={16} />}
          {running ? "En cours..." : "Configurer et confirmer"}
        </Button>
      </div>

      {/* Tâche 1254 — Pas encore de conteneur OVH ? La marche à suivre est ici,
          à côté du bouton, pas dans un document que personne n'ouvre. */}
      <button
        onClick={() => setGuideOpen((v) => !v)}
        className="mt-3 text-xs font-semibold text-[#002FA7] hover:underline"
        data-testid="ovh-guide-toggle"
      >
        {guideOpen ? "Masquer la marche à suivre" : "Je n'ai pas encore de conteneur OVH — marche à suivre"}
      </button>
      {guideOpen && (
        <ol className="mt-2 space-y-2 border-l-2 border-[#002FA7]/20 pl-3" data-testid="ovh-guide-steps">
          {OVH_STEPS.map((e) => (
            <li key={e.titre}>
              <p className="text-xs font-semibold text-zinc-900">{lang === "en" ? e.titre_en : e.titre}</p>
              <p className="text-xs text-zinc-600 leading-relaxed">{lang === "en" ? e.detail_en : e.detail}</p>
            </li>
          ))}
          <li className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            {tr(
              "Pièges : une région autre que BHS n'est pas conforme à la loi 25 · un rôle en lecture seule laisse passer l'envoi mais fait échouer la vérification · un nom de conteneur approximatif donne exactement le même refus qu'une mauvaise clé.",
              "Traps: any region other than BHS breaks Law 25 · a read-only role lets the upload through but fails the verification · an approximate container name gives exactly the same refusal as a wrong key.",
            )}
          </li>
        </ol>
      )}

      {result && (
        <div className="mt-4 space-y-2" data-testid="offsite-setup-steps">
          {result.steps.map((s) => (
            <div key={s.label} className="flex items-start gap-2 text-sm">
              {s.ok ? (
                <CheckCircle size={18} className="text-teal-600 shrink-0 mt-0.5" weight="fill" />
              ) : (
                <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" weight="fill" />
              )}
              <div className="min-w-0">
                <span className="font-medium text-zinc-900">{s.label}</span>
                <span className="text-zinc-600"> — {s.detail}</span>
                {s.hint && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                    {s.hint}
                  </p>
                )}
              </div>
            </div>
          ))}
          {result.confirmed && (
            <p
              className="text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2"
              data-testid="offsite-setup-confirmed"
            >
              {tr("Copie hors site confirmée — conteneur ", "Off-site copy confirmed — container ")}<strong>{result.bucket}</strong>
              {tr(" (région ", " (region ")}{result.region}
              {tr("), objet ", "), object ")}<code className="text-xs">{result.ovh_key}</code>.{" "}
              {tr("Les vérifications nocturnes surveilleront désormais cette copie.", "The nightly checks will watch this copy from now on.")}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default OffsiteSetupCard;
